import type { QueryKey } from '@tanstack/react-query';

import { queryClient, useQuery } from '../../../ReactQuery';
import { get } from '../../../redux/api';
import type { GetActiveBannersResponse } from '../../../../shared/types/routes/banners';
import type { BannerPlacement } from '../../../../typings/enums';

export type ActiveBannerPlacement = BannerPlacement.Login | BannerPlacement.Dashboard;

//  Banners aren't updated often, so we can cache them for a while.
const ACTIVE_BANNERS_STALE_TIME = 1000 * 60 * 5; // 5 minutes

function getActiveBanners(placement?: ActiveBannerPlacement) {
  return get<GetActiveBannersResponse>(
    '/api/banners/active',
    false,
    placement ? { placement } : undefined,
  );
}

export const getActiveBannersQueryKey = (placement?: ActiveBannerPlacement): QueryKey => (
  placement ? ['banners', 'active', placement] : ['banners', 'active']
);

/**
 * Invalidate every placement-keyed active banner query so the login and dashboard
 * caches refetch after a banner mutation. Passing the placement-less key prefix
 * matches all `['banners', 'active', <placement>]` entries.
 */
export const invalidateActiveBanners = () =>
  queryClient.invalidateQueries({ queryKey: getActiveBannersQueryKey() });

/**
 * Hook for fetching active banners for a given placement.
 */
export default function useActiveBanners(placement?: ActiveBannerPlacement) {
  return useQuery<GetActiveBannersResponse>({
    queryFn: () => getActiveBanners(placement),
    queryKey: getActiveBannersQueryKey(placement),
    staleTime: ACTIVE_BANNERS_STALE_TIME,
  });
}
